import { WmOption, WmSelect, WmFlyout } from "@watermarkinsights/ripple-react";
import { useIntl } from "react-intl";
import translate from "@/i18n/translate";
import { ResponseRateByCourseSection } from "../ResponseRateOverview";
import { ResponseRateFlyoutCharts } from "./ResponseFlyoutCharts";
import { useEffect, useReducer } from "react";

export interface ResponseRateFlyoutProps {
  CloseDrilldownView: () => void;
  responseRateByCourseSection: ResponseRateByCourseSection[];
  open: boolean;
}

enum SortBy {
  ResponseRateDesc = "response-rate-desc",
  ResponseRateAsc = "response-rate-asc",
  CourseSectionAsc = "course-section-asc",
  CourseSectionDesc = "course-section-desc"
}

interface State {
  sortBy: string;
  sortedResponseRateByCourseSection: ResponseRateByCourseSection[];
}
interface Action {
  type: string;
  sortBy: string;
  sortedResponseRateByCourseSection: ResponseRateByCourseSection[];
}

const defaultAction: Action = {
  type: "",
  sortBy: "",
  sortedResponseRateByCourseSection: []
};

const initialState: State = {
  sortBy: SortBy.ResponseRateDesc,
  sortedResponseRateByCourseSection: []
};

const stateReducer = (state: State, action: Action) => {
  switch (action.type) {
    case "setSortBy":
      return { ...state, sortBy: action.sortBy };
    case "setSortedResponseRateByCourseSection":
      return {
        ...state,
        sortedResponseRateByCourseSection:
          action.sortedResponseRateByCourseSection
      };
    default:
      return state;
  }
};
function sortCourseSections(
  courseSection1: ResponseRateByCourseSection,
  courseSection2: ResponseRateByCourseSection,
  sortBy: string
) {
  switch (sortBy) {
    case SortBy.ResponseRateDesc:
      return (
        courseSection2.respondedPercentage - courseSection1.respondedPercentage
      );
    case SortBy.ResponseRateAsc:
      return (
        courseSection1.respondedPercentage - courseSection2.respondedPercentage
      );
    case SortBy.CourseSectionAsc:
    case SortBy.CourseSectionDesc:
      let title1 = courseSection1.courseSection.toLowerCase();
      let title2 = courseSection2.courseSection.toLowerCase();
      if (title1 > title2) {
        return sortBy === SortBy.CourseSectionAsc ? 1 : -1;
      }
      if (title1 < title2) {
        return sortBy === SortBy.CourseSectionAsc ? -1 : 1;
      }
      return 0;

    default:
      return 0;
  }
}

export function ResponseRateFlyout({
  CloseDrilldownView,
  responseRateByCourseSection,
  open
}: ResponseRateFlyoutProps) {

  const [state, dispatch] = useReducer(stateReducer, initialState);

  const flyoutHeading = useIntl().formatMessage({
    id: "app.home.responseRateOverview.flyoutHeading"
  });
  const flyoutWidth = "1025px";

  function updateSortByValue(sortByValue: string) {
    dispatch({ ...defaultAction, type: "setSortBy", sortBy: sortByValue });
  }

  const closeDrilldownView = () => {
    CloseDrilldownView();
  };

  useEffect(() => {
    const sorted = responseRateByCourseSection.sort(
      (courseSection1, courseSection2) =>
        sortCourseSections(courseSection1, courseSection2, state.sortBy)
    );
    dispatch({
      ...defaultAction,
      type: "setSortedResponseRateByCourseSection",
      sortedResponseRateByCourseSection: sorted
    });
  }, [state.sortBy, responseRateByCourseSection]);

  return (
    <WmFlyout
      id="drill-down-view"
      heading={flyoutHeading}
      open={open}
      flyoutWidth={flyoutWidth}
      onWmFlyoutCloseTriggered={closeDrilldownView}
      onWmFlyoutPrimaryTriggered={closeDrilldownView}
      className="ces-flyout-position"
      primaryText="Close"
    >
      <div className="response-rate-flyout-container">
        <section id="org-table-catalog" className="flyout-body">
          <SortSelectMenu
            updateSortByValue={updateSortByValue}
            sortBy={state.sortBy}
          />
          <div className="course-section-charts">
            {state.sortedResponseRateByCourseSection.map(
              (responseRate: ResponseRateByCourseSection, index) => {
                return (
                  <ResponseRateFlyoutCharts
                    key={`${responseRate.id}`}
                    responseRate={responseRate}
                    index={index}
                    courseSectionLength={
                      state.sortedResponseRateByCourseSection.length
                    }
                  />
                );
              }
            )}
          </div>
        </section>
      </div>
    </WmFlyout>
  );
}

interface SortyByOption {
  id: string;
  value: JSX.Element;
}
const selectOptions: Array<SortyByOption> = [
  {
    id: SortBy.ResponseRateDesc,
    value: translate("app.home.responseRateOverview.flyout.highToLow")
  },
  {
    id: SortBy.ResponseRateAsc,
    value: translate("app.home.responseRateOverview.flyout.lowToHigh")
  },
  {
    id: SortBy.CourseSectionAsc,
    value: translate("app.home.responseRateOverview.flyout.courseSectionAZ")
  },
  {
    id: SortBy.CourseSectionDesc,
    value: translate("app.home.responseRateOverview.flyout.courseSectionZA")
  }
];

interface SortSelectMenuProps {
  updateSortByValue: (id: string) => void;
  sortBy: string;
}
function SortSelectMenu({ updateSortByValue, sortBy }: SortSelectMenuProps) {
  return (
    <div className="sort-by-select">
      <WmSelect label="Sort by" labelPosition="left">
        {selectOptions.map((option: SortyByOption, index: number) => {
          return (
            <WmOption
              value={option.id}
              selected={sortBy === option.id}
              key={option.id}
              onClick={() => updateSortByValue(option.id)}
            >
              {option.value}
            </WmOption>
          );
        })}
      </WmSelect>
    </div>
  );
}
